import React, { useEffect, useState } from 'react';
import { Box, styled, Typography, Collapse } from '@mui/material';
import { useNavigate, useLocation, NavigateFunction, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import { useMediaQuery } from "@react-hook/media-query";

// COMPONENT 👇
import { colors } from '../../constants/colors';
import { RouteImage } from '../../assets/routeImage';
import { setFalse, setTrue } from '../../store/slice/navbarActive/navbarSlice';
import { Menu, menuProps, subMenuProps, thirdMenuProps } from '../../utils/Menu';

// REDUX
import { toggle } from '../../store/slice/menuActive/menuActive';
import { setNumber } from '../../store/slice/selected-menu/selectedMenu.slice';
import { ResourcePathApi } from '../../constants/resource.pathapi';

const LogoBox = styled(Box)(({ theme }) => ({
    width: "100%",
    height: "80px",
    borderBottom: `1px solid ${colors.lampang_primary}`,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    [theme.breakpoints.down('mobileXl')]: {
        justifyContent: 'center',
        padding: "0 1rem",
        'span': {
            display: "block"
        }
    }
}));

const MenuBox = styled('ul')({
    width: "100%"
});

const NavBar = styled('nav')({
    width: '100%'
});

const Navbar: React.FC = () => {
    const navigate: NavigateFunction = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const isMediumScreen = useMediaQuery("(max-width: 768px)");
    const permission = useSelector((state: any) => state?.permission?.permission);
    const [pathUrl, setPathUrl] = useState<any>(null);

    const active = useSelector((state: any) => state.navbar.value);
    const openMenu = useSelector((state: any) => state.menu.value);
    const selectedMenuId = useSelector((state: any) => state.selectedMenu.menuId);
    // const [activeSubMenu, setActiveSubMenu] = useState<any>(null);
    // const handleSubMenuClick = (path: string, subMenuId: any) => {
    //     setActiveSubMenu(subMenuId);
    //     navigate(path);
    // };

    const handleToggleMenu = (menuId: number) => {
        dispatch(toggle());
        dispatch(setNumber(menuId));
    };

    useEffect(() => {
        if (isMediumScreen) {
            dispatch(setFalse());
        } else {
            dispatch(setTrue());
        }
    }, [isMediumScreen, dispatch]);

    useEffect(() => {
        const fetchPath = async () => {
            const path = await ResourcePathApi();
            setPathUrl(path);
        };
        fetchPath();
    }, []);

    return (
        <motion.aside
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ stiffness: 100, ease: "backInOut" }}
            className={`min-h-screen z-30 bg-white ${active ? 'w-[224px]' : 'w-0'} z-[99] duration-200 shadow-md`}
        >
            <LogoBox>
                <div className='flex justify-center items-center z-[999]'>
                    <Link to='/auth/statistics'>
                        <img width={56} height={56} src={pathUrl && `${pathUrl.REACT_APP_BASE_API}/app-logo`} alt="app-logo" />
                    </Link>
                </div>
            </LogoBox>
            <MenuBox
                className={`${!active && 'opacity-0 pointer-events-none'} delay-150 duration-300`}
            >
                {Menu.map((list: menuProps, index: number) => (
                    <Box
                        sx={{
                            display: permission?.find((permissionList: any) => permissionList?.code === list.code)?.permit === false ? 'none' : ''
                        }}
                        className='relative'
                        key={list.id}
                    >
                        <NavBar
                            onClick={() => {
                                if (list.subMenu.length > 0) {
                                    if (list.id === 4) {
                                        navigate(list.path);
                                        handleToggleMenu(list.id);
                                    } else {
                                        handleToggleMenu(list.id);
                                    }
                                } else {
                                    navigate(list.path);
                                }
                            }}
                            className={`${location.pathname === list.path || location.pathname.includes(list.path) ? 'bg-sub_primary' : ''} relative group h-[48px] px-[20px] cursor-pointer flex flex-col justify-center items-start group hover:bg-sub_primary`}
                        >
                            <motion.div
                                initial={{ x: -30 }}
                                animate={{ x: 0 }}
                                transition={{ ease: "easeInOut", delay: .2, stiffness: 200 }}
                                className='flex justify-between w-full items-center gap-x-2'
                            >
                                <div
                                    className={`${location.pathname === list.path || location.pathname.includes(list.path) ? 'after:opacity-100 duration-300 delay-200' : ''} w-auto after:opacity-0 after:w-[0.2rem] after:h-full after:bg-primary after:absolute after:left-0 after:top-0 duration-200`}
                                >
                                    <img
                                        src={list?.icon}
                                        width="20"
                                        height="20"
                                        style={{ maxWidth: '20px', height: 'auto' }}
                                        alt="icons"
                                    />
                                </div>
                                <motion.div className='relative text-start w-full'>
                                    <Typography
                                        style={{ transitionDelay: `${index + 4}00ms` }}
                                        className={`text-primary whitespace-pre duration-500 ${!active && 'opacity-0 translate-x-28 overflow-hidden'}`}
                                        variant='body1'
                                    >
                                        {list.menuName}
                                    </Typography>
                                    <Typography
                                        className={`${active && 'hidden'} group-hover:w-fit group-hover:left-2 group-hover:px-2 group-hover:py-1 duration-200 absolute top-1/2 translate-x-4 translate-y-[-50%] bg-white text-[#000] left-48 drop-shadow-md w-0 overflow-hidden px-0 py-0 h-auto font-semibold whitespace-pre rounded-md`}
                                        variant='body1'
                                    >
                                        {list?.menuName}
                                    </Typography>
                                </motion.div>
                                <motion.div className={`${active ? 'block' : 'hidden'}`}>
                                    {list.subMenu.length > 0 && (
                                        <Box
                                            className={`w-auto ${openMenu && list.id === selectedMenuId && 'rotate-180'}`}
                                        >
                                            <img
                                                style={{ maxWidth: 20 }}
                                                className='max-w-20'
                                                src={RouteImage.downArrow}
                                                alt="icons"
                                            />
                                        </Box>
                                    )}
                                </motion.div>
                            </motion.div>
                        </NavBar>
                        {/* Submenu */}
                        <Collapse in={selectedMenuId === list.id && openMenu} timeout="auto">
                            <div className={`w-full relative flex flex-col ${!active && 'hidden'}`}>
                                {list.subMenu?.map((subMenu: subMenuProps) => (
                                    <React.Fragment key={subMenu.subMenuId}>
                                        <Box
                                            onClick={() => navigate(subMenu.path)}
                                            className={`${location.pathname === subMenu.path ? 'bg-sub_primary' : ''} h-auto w-full cursor-pointer hover:bg-sub_primary`}
                                        >
                                            <li className={`pl-12 py-3 pt-3 ${permission?.find((list: any) => list?.code === subMenu.code)?.permit === false ? 'hidden' : ''}`}>
                                                <Typography
                                                    className={`${location.pathname === subMenu.path ? 'text-[#000] text-bold' : 'text-primary'} whitespace-pre`}
                                                    variant='body1'
                                                >
                                                    {subMenu.subMenuName}
                                                </Typography>
                                            </li>
                                        </Box>

                                        {/* Third Menu */}
                                        <Collapse in={location.pathname.includes(subMenu.path)} timeout="auto" unmountOnExit>
                                            {subMenu.subThirdMenu?.map((thirdMenu: thirdMenuProps) => (
                                                <Box
                                                    onClick={() => navigate(`/auth/setting/${thirdMenu.path}`)}
                                                    className={`${location.pathname === thirdMenu.path || location.pathname.includes(thirdMenu.path) ? 'bg-sub_primary' : ''} h-auto w-full cursor-pointer hover:bg-sub_primary`}
                                                    key={thirdMenu.subThirdMenuId}
                                                >
                                                    <li
                                                        className={`pl-16 py-3 pt-3 flex items-center justify-start gap-x-2 ${permission?.find((list: any) => list?.code === thirdMenu.code)?.permit === false ? 'hidden' : ''}`}
                                                    >
                                                        <Typography className='text-primary' variant='body1'>
                                                            <img
                                                                style={{ maxWidth: "24px" }}
                                                                src={RouteImage.list}
                                                                alt="list"
                                                            />
                                                        </Typography>
                                                        <Typography
                                                            className={`${location.pathname === thirdMenu.path || location.pathname.includes(thirdMenu.path) ? 'text-[#190e0e] text-bold' : 'text-primary'} duration-500`}
                                                            variant='body1'
                                                        >
                                                            {thirdMenu.subThirdMenuName}
                                                        </Typography>
                                                    </li>
                                                </Box>
                                            ))}
                                        </Collapse>
                                    </React.Fragment>
                                ))}
                            </div>
                        </Collapse>
                    </Box>
                ))}
            </MenuBox>
        </motion.aside>
    );
}

export default Navbar;
